import { useNavigate, useParams } from 'react-router-dom'
import React, { useState, useEffect } from 'react'
import { 
  ArrowLeft,
  Loader2,
  AlertCircle,
  Edit3
} from 'lucide-react'
import { apiClient, handleApiError } from '@/lib/api/index'
import InterviewScheduleModal from '@/components/interview/InterviewScheduleModal'

export default function EditInterviewPage() {
  const params = useParams()
  const navigate = useNavigate()
  const interviewId = params?.id as string
  
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [interviewData, setInterviewData] = useState<any>(null)
  const [showEditModal, setShowEditModal] = useState(false)

  useEffect(() => {
    if (interviewId) {
      loadInterview()
    }
  }, [interviewId])

  const loadInterview = async () => {
    try {
      console.log('🔄 Loading interview for edit:', interviewId)
      setLoading(true)
      setError(null)
      
      // Fetch interview details and assigned problems in parallel
      const [interviewResponse, codingResponse, databaseResponse] = await Promise.all([
        apiClient.getInterviewResponse(interviewId),
        apiClient.getInterviewCodingChallenges(interviewId),
        apiClient.getInterviewDatabaseChallenges(interviewId)
      ])
      
      console.log('🔄 Interview API responses:', {
        interview: interviewResponse.success,
        coding: codingResponse.success,
        database: databaseResponse.success
      })
      
      if (interviewResponse.success && interviewResponse.data) {
        const data = {
          ...interviewResponse.data,
          codingProblems: codingResponse.success ? codingResponse.data : [],
          databaseProblems: databaseResponse.success ? databaseResponse.data : []
        }
        
        console.log('✅ Interview data prepared for edit:', data)
        setInterviewData(data)
        setShowEditModal(true) // Show the modal immediately
      } else {
        setError('Interview not found')
      }
    } catch (err) {
      console.error('Failed to load interview:', err)
      setError('Failed to load interview data')
    } finally {
      setLoading(false)
    }
  }

  const handleModalClose = () => {
    setShowEditModal(false)
    navigate(`/recruiter/interviews/${interviewId}`)
  }

  const handleInterviewUpdated = () => {
    setShowEditModal(false)
    navigate(`/recruiter/interviews/${interviewId}`)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Loading interview...</p>
        </div>
      </div>
    )
  }

  if (error && !interviewData) {
    return (
      <div className="max-w-4xl mx-auto py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 flex items-start space-x-3">
          <AlertCircle className="w-6 h-6 text-red-500 mt-1" />
          <div>
            <h3 className="text-lg font-medium text-red-800">Error Loading Interview</h3>
            <p className="text-red-700 mt-2">{error}</p>
            <button
              onClick={() => navigate('/recruiter/interviews')}
              className="mt-4 text-red-600 hover:text-red-500 underline"
            >
              Back to Interviews
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate(`/recruiter/interviews/${interviewId}`)}
          className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Edit Interview</h1>
          <p className="text-gray-600">Update interview details and settings</p>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Loading state when modal is not yet shown */}
      {!showEditModal && !error && (
        <div className="text-center py-8">
          <Edit3 className="w-12 h-12 text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Preparing edit form...</p>
        </div>
      )}

      {/* Interview Schedule Modal for editing */}
      {interviewData && (
        <InterviewScheduleModal
          open={showEditModal}
          onClose={handleModalClose}
          isEdit={true}
          interviewId={interviewId}
          editData={interviewData}
          candidateId={interviewData.candidate?.id?.toString() || interviewData.candidate?.uuid}
          candidateName={`${interviewData.candidate?.first_name || ''} ${interviewData.candidate?.last_name || ''}`.trim()}
          positionId={interviewData.position?.id?.toString() || interviewData.position?.uuid}
          positionData={{
            id: interviewData.position?.id?.toString() || interviewData.position?.uuid || '',
            title: interviewData.position?.title || '',
            department: interviewData.position?.department || ''
          }}
        />
      )}
    </div>
  )
}